import styled, { keyframes } from "styled-components";

import {
  ANIMATION,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../../utils/constants";
import { BaseButton } from "../../../../../components/Button";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// A composited transform, never an animated width, so the entrance never triggers layout.
const growX = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
  }

  margin-bottom: 0;
`;

export const Header = styled(BaseButton)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  width: 100%;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border: none;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-tab-background);
  color: var(--color-text-default);
  cursor: pointer;
  text-align: left;
  transition: ${ANIMATION.transitions.all};

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const Chevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  color: var(--color-text-secondary);
  transition: transform ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};
  transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "180deg")});

  & > svg {
    font-size: var(--text-xl);
  }
`;

export const HeaderTitle = styled.span`
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const CollapseLabel = styled.span`
  margin-left: auto;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.xl}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  animation: ${fadeIn} ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: 1fr 1fr;
    gap: ${SPACING.xxl}px;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  min-width: 0;
`;

export const ColumnHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const ColumnTitle = styled.h3`
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const ColumnSubtitle = styled.span`
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const RowList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Row = styled.li`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "name value"
    "track track";
  align-items: center;
  gap: ${SPACING.xs}px ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: 150px minmax(0, 1fr) 88px;
    grid-template-areas: "name track value";
    gap: ${SPACING.md}px;
  }
`;

export const Name = styled.span`
  grid-area: name;
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
  font-size: var(--text-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-default);

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Swatch = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

export const Value = styled.span<{ $muted?: boolean }>`
  grid-area: value;
  justify-self: end;
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  color: ${({ $muted }) =>
    $muted ? "var(--color-text-secondary)" : "var(--color-text-default)"};
`;

export const Track = styled.div`
  grid-area: track;
  position: relative;
  display: flex;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: var(--color-background-3);
  overflow: hidden;
`;

export const SegmentReveal = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transform-origin: left center;
  animation: ${growX} ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const DebtMixFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  border-radius: 999px;
  width: ${({ $width }) => `${$width}%`};
  background: ${({ $color }) => $color};
  transform-origin: left center;
  animation: ${growX} ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PrincipalSegment = styled.div<{ $width: number }>`
  height: 100%;
  width: ${({ $width }) => `${$width}%`};
  background: var(--color-success);
`;

export const InterestSegment = styled.div<{ $width: number }>`
  height: 100%;
  width: ${({ $width }) => `${$width}%`};
  background: var(--color-warning);
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.md}px;
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const LegendSwatch = styled.span<{ $tone: "principal" | "interest" }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $tone }) =>
    $tone === "principal" ? "var(--color-success)" : "var(--color-warning)"};
`;
